import React, { useCallback, useEffect, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Eye,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  X,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import {
  COURSE_LEVELS,
  COURSE_STATUSES,
  formatCourseDate,
  normalizeCourse,
} from './courseUtils';

const THEME = getThemeColor('primary');

const Courses = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('createdDateTime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const fetchList = useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getEducationCoursesList({
          pageNumber,
          pageSize: pagination.pageSize,
          searchTerm,
          category,
          level,
          status,
          sortBy,
          sortOrder,
        });
        if (!res.success) {
          setRows([]);
          setError(res.error || res.message || 'Failed to load courses');
          return;
        }
        const payload = res.data ?? res.result ?? {};
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
        setRows(list.map(normalizeCourse).filter(Boolean));
        setPagination((prev) => ({
          ...prev,
          pageNumber: payload.pageNumber || pageNumber,
          pageSize: payload.pageSize || prev.pageSize,
          totalRecords: payload.totalRecords || 0,
          totalPages: payload.totalPages || 0,
          hasPreviousPage: Boolean(payload.hasPreviousPage),
          hasNextPage: Boolean(payload.hasNextPage),
        }));
      } catch (err) {
        setRows([]);
        setError(toUserMessage(err, ERROR_MESSAGES.load));
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchTerm, category, level, status, sortBy, sortOrder]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openConfirm = (type, course) => setConfirmAction({ type, course });

  const runConfirm = async () => {
    if (!confirmAction?.course) return;
    const { type, course } = confirmAction;
    setActionBusy(true);
    try {
      let res;
      if (type === 'activate') res = await apiService.activateEducationCourse(course.id);
      else if (type === 'inactivate') res = await apiService.inactivateEducationCourse(course.id);
      else if (type === 'delete') res = await apiService.deleteEducationCourse(course.id);
      else return;

      if (!res.success) {
        showError(res.error || res.message || 'Action failed');
        return;
      }
      showSuccess(res.message || 'Updated successfully');
      setConfirmAction(null);
      await fetchList(pagination.pageNumber);
    } catch (err) {
      showError(toUserMessage(err, ERROR_MESSAGES.request));
    } finally {
      setActionBusy(false);
    }
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const confirmCopy =
    confirmAction?.type === 'delete'
      ? 'Are you sure you want to delete this course?'
      : confirmAction?.type === 'inactivate'
        ? 'Are you sure you want to make this course inactive?'
        : confirmAction?.type === 'activate'
          ? 'Are you sure you want to activate this course?'
          : '';

  if (loading && rows.length === 0) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner">
        <div className="edu-header-row">
          <ModulePageHeader
            icon={BookOpen}
            title="Courses"
            subtitle="Create and manage education courses for your partners"
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <button
            type="button"
            className="edu-btn edu-btn--primary"
            onClick={() => navigate('/education/courses/new')}
          >
            <Plus className="w-4 h-4" />
            Add Course
          </button>
        </div>

        <ModuleAlert message={error} />

        <div className="edu-toolbar">
          <input
            className="edu-input"
            placeholder="Search courses…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <input
            className="edu-input"
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <select
            className="edu-select"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            aria-label="Level filter"
          >
            <option value="">All levels</option>
            {COURSE_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="edu-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All statuses</option>
            {COURSE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <section className="module-card edu-card">
          {rows.length === 0 ? (
            <div className="edu-empty">
              <BookOpen className="edu-empty__icon" />
              <p>No courses yet. Create your first course to get started.</p>
              <button
                type="button"
                className="edu-btn edu-btn--primary"
                onClick={() => navigate('/education/courses/new')}
              >
                <Plus className="w-4 h-4" />
                Add Course
              </button>
            </div>
          ) : (
            <>
              <div className="edu-table-wrap">
                <table className="edu-table">
                  <thead>
                    <tr>
                      <th>
                        <button type="button" className="edu-back" onClick={() => toggleSort('title')}>
                          Course Title
                        </button>
                      </th>
                      <th>Category</th>
                      <th>Level</th>
                      <th>Duration</th>
                      <th>Language</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th className="edu-table__actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="edu-title-cell">{row.title}</td>
                        <td>{row.category || '—'}</td>
                        <td>{row.level || '—'}</td>
                        <td>{row.duration || '—'}</td>
                        <td>{row.language || '—'}</td>
                        <td>
                          <span
                            className={`edu-status ${row.isActive ? 'is-active' : 'is-inactive'}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>{formatCourseDate(row.createdDateTime)}</td>
                        <td>{formatCourseDate(row.updatedDateTime)}</td>
                        <td className="edu-table__actions">
                          <button
                            type="button"
                            className="edu-icon-btn"
                            title="View"
                            aria-label="View"
                            onClick={() => navigate(`/education/courses/${row.id}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="edu-icon-btn"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() => navigate(`/education/courses/${row.id}/edit`)}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {row.isActive ? (
                            <button
                              type="button"
                              className="edu-icon-btn"
                              title="Inactivate"
                              aria-label="Inactivate"
                              onClick={() => openConfirm('inactivate', row)}
                            >
                              <PowerOff className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="edu-icon-btn is-success"
                              title="Activate"
                              aria-label="Activate"
                              onClick={() => openConfirm('activate', row)}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="edu-icon-btn is-danger"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => openConfirm('delete', row)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="edu-pagination">
                <span>
                  Showing page {pagination.pageNumber} of {Math.max(pagination.totalPages, 1)} (
                  {pagination.totalRecords} total)
                </span>
                <div className="edu-pagination__btns">
                  <button
                    type="button"
                    className="edu-btn edu-btn--ghost edu-btn--sm"
                    disabled={!pagination.hasPreviousPage || loading}
                    onClick={() => fetchList(pagination.pageNumber - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="edu-btn edu-btn--ghost edu-btn--sm"
                    disabled={!pagination.hasNextPage || loading}
                    onClick={() => fetchList(pagination.pageNumber + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {confirmAction ? (
        <div className="edu-modal" role="presentation">
          <button
            type="button"
            className="edu-modal__backdrop"
            onClick={() => !actionBusy && setConfirmAction(null)}
            aria-label="Close"
          />
          <div className="edu-modal__panel" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>Confirm</h2>
              <button
                type="button"
                className="edu-icon-btn"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="edu-modal__body">
              <p>
                {confirmCopy}
                {confirmAction.course?.title ? (
                  <>
                    {' '}
                    <strong>{confirmAction.course.title}</strong>
                  </>
                ) : null}
              </p>
            </div>
            <div className="edu-modal__foot">
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`edu-btn ${
                  confirmAction.type === 'delete' ? 'edu-btn--danger' : 'edu-btn--primary'
                }`}
                disabled={actionBusy}
                onClick={runConfirm}
              >
                {actionBusy ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Courses;
